import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import { FormattedMessage, IntlProvider } from 'react-intl';
import classNames from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';

import resourcesConfig from '../../resources';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { extractWhitelabelSubdomain } from '../../utils/helpers';
import { getErrorTitle, getErrorDescription } from '../../selectors';
import { apiResponseCodes, serverErrorCodes } from '../../constants/globals';
import { clearApiErrorResponseAction, fetchBrandResourcesAction } from '../../actions/common';
import ThemeContext from '../../containers/ThemeContext';
import pagesRoutes from '../../routes/pages';

const NonExistentSubdomainFallback = asyncRoute(() =>
  import('../Fallback/NonExistentSubdomainFallback'),
);
const TermsOfService = asyncRoute(() => import('./TermsOfService'));
const PrivacyPolicy = asyncRoute(() => import('./PrivacyPolicy'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));

const Pages = ({ actions, match, location, history, resources, errorResponse }) => {
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorContent, setErrorContent] = useState({});
  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);

  useEffect(() => {
    if (!isEmpty(errorResponse)) {
      const { code, message } = errorResponse;
      const draftErrorContent = {
        title: getErrorTitle({ source: resources, code }),
        description: getErrorDescription({ source: resources, code, message }),
      };
      setShowErrorPopup(true);
      setErrorContent(draftErrorContent);
    }
  }, [errorResponse]);

  useEffect(() => {
    const subDomain = extractWhitelabelSubdomain();
    if (subDomain) {
      actions.fetchBrandResources({ brandId: subDomain }).then((response) => {
        if (
          !apiResponseCodes.SUCCESS.includes(get(response, 'status', '')) &&
          !isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)
        ) {
          // incase if sub domain is not resolved
          setShowNonExistentSubdomainFallback(true);
        }
      });
    }
  }, []);

  const onCloseExceptionComponent = () => {
    actions.clearApiErrorResponse().then(() => {
      setShowErrorPopup(false);
      setErrorContent({});
    });
  };

  const handleNonExistentSubdomainClick = () => {
    window.location.reload();
  };

  const renderRoutes = () => (
    <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Switch location={location}>
          <Route
            exact
            path={`${match.path}/${pagesRoutes.containers.termsOfService}`}
            component={TermsOfService}
          />
          <Route
            exact
            path={`${match.path}/${pagesRoutes.containers.privacyPolicy}`}
            component={PrivacyPolicy}
          />
        </Switch>
      )}
    </IntlProvider>
  );

  return (
    <div className={classNames('pages')} id="pages-container">
      <ThemeContext.Provider>
        {renderRoutes()}
        {showErrorPopup && (
          <ExceptionComponent
            pageType="HalfSize"
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={onCloseExceptionComponent}
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
          />
        )}
      </ThemeContext.Provider>
    </div>
  );
};

const mapStateToProps = (state) => ({
  resources: get(state, 'common.resources', resourcesConfig),
  errorResponse: get(state, 'common.errorResponse', {}),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      clearApiErrorResponse: clearApiErrorResponseAction,
      fetchBrandResources: fetchBrandResourcesAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Pages);
